import { useEffect, useState } from 'react';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { notificationsPrefsApi } from '../../../api/client';
import type { NotificationPreferences } from '../../../api/client';

interface ToggleProps {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}

function Toggle({ checked, onChange, disabled }: ToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 focus:ring-offset-surface-1 ${
        checked ? 'bg-brand' : 'bg-surface-4'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-4.5' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

interface SettingRowProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}

function SettingRow({ label, description, checked, onChange, disabled }: SettingRowProps) {
  return (
    <div className="flex items-center justify-between py-3">
      <div className="flex-1 min-w-0 pr-4">
        <p className="text-sm font-medium text-gray-200">{label}</p>
        <p className="text-xs text-gray-500 mt-0.5">{description}</p>
      </div>
      <Toggle checked={checked} onChange={onChange} disabled={disabled} />
    </div>
  );
}

export function NotificationsSettingsPage() {
  const [prefs, setPrefs] = useState<NotificationPreferences>({
    betting_alerts: true,
    bankroll_alerts: true,
    calibration_alerts: true,
    product_updates: false,
    email_enabled: true,
    in_app_enabled: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    setLoading(true);
    notificationsPrefsApi.get()
      .then(setPrefs)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function handleToggle(field: keyof NotificationPreferences, value: boolean) {
    const prev = prefs;
    setPrefs((p) => ({ ...p, [field]: value }));
    setSaving(true);
    setSaveStatus('idle');
    try {
      const updated = await notificationsPrefsApi.update({ [field]: value });
      setPrefs(updated);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch {
      setPrefs(prev);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-8 space-y-8">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-100">Notifications</h2>
          <p className="text-sm text-gray-500 mt-1">Choose what alerts and updates you receive.</p>
        </div>
        <div className="flex items-center gap-2 h-7">
          {saving && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
          {saveStatus === 'success' && (
            <span className="flex items-center gap-1 text-xs text-win">
              <CheckCircle className="w-3.5 h-3.5" /> Saved
            </span>
          )}
          {saveStatus === 'error' && (
            <span className="flex items-center gap-1 text-xs text-loss">
              <AlertCircle className="w-3.5 h-3.5" /> Failed to save
            </span>
          )}
        </div>
      </div>

      {/* Notification Categories */}
      <div className="op-panel p-4">
        <h3 className="text-sm font-medium text-gray-300 mb-1">Alert Types</h3>
        <p className="text-xs text-gray-500 mb-4">Select which types of alerts you want to receive.</p>
        <div className="divide-y divide-surface-4">
          <SettingRow
            label="Betting Alerts"
            description="Notifications for high-confidence bet recommendations"
            checked={prefs.betting_alerts ?? true}
            onChange={(v) => handleToggle('betting_alerts', v)}
            disabled={saving}
          />
          <SettingRow
            label="Bankroll Alerts"
            description="Warnings when your bankroll reaches thresholds"
            checked={prefs.bankroll_alerts ?? true}
            onChange={(v) => handleToggle('bankroll_alerts', v)}
            disabled={saving}
          />
          <SettingRow
            label="Calibration Alerts"
            description="Updates when model calibration runs complete"
            checked={prefs.calibration_alerts ?? true}
            onChange={(v) => handleToggle('calibration_alerts', v)}
            disabled={saving}
          />
          <SettingRow
            label="Product Updates"
            description="New features, improvements, and platform news"
            checked={prefs.product_updates ?? false}
            onChange={(v) => handleToggle('product_updates', v)}
            disabled={saving}
          />
        </div>
      </div>

      {/* Notification Channels */}
      <div className="op-panel p-4">
        <h3 className="text-sm font-medium text-gray-300 mb-1">Channels</h3>
        <p className="text-xs text-gray-500 mb-4">Control how you receive notifications.</p>
        <div className="divide-y divide-surface-4">
          <SettingRow
            label="Email Notifications"
            description="Receive alerts via email"
            checked={prefs.email_enabled ?? true}
            onChange={(v) => handleToggle('email_enabled', v)}
            disabled={saving}
          />
          <SettingRow
            label="In-App Notifications"
            description="Show alerts in the Navyrx Sports notification panel"
            checked={prefs.in_app_enabled ?? true}
            onChange={(v) => handleToggle('in_app_enabled', v)}
            disabled={saving}
          />
        </div>
      </div>
    </div>
  );
}
